import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, CircleAlert, KeyRound, Mail, RefreshCcw } from 'lucide-react';
import { toast } from 'sonner';

import { auth } from '../lib/api';
import { BrandLogoWithWordmark } from '../components/BrandLogo';
import { Button, Field, Input } from '../components/ui-kit';

const RESET_EMAIL_STORAGE_KEY = 'td:pending-password-reset';

const readPendingResetEmail = () => {
  if (typeof window === 'undefined') return '';
  try {
    return window.sessionStorage.getItem(RESET_EMAIL_STORAGE_KEY) || '';
  } catch (error) {
    return '';
  }
};

const savePendingResetEmail = (email) => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(RESET_EMAIL_STORAGE_KEY, email);
  } catch (error) {
    // Storage can be unavailable in private or embedded contexts.
  }
};

const clearPendingResetEmail = () => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(RESET_EMAIL_STORAGE_KEY);
  } catch (error) {
    // Storage can be unavailable in private or embedded contexts.
  }
};

const extractApiMessage = (error) => {
  const data = error?.response?.data;
  if (typeof data?.error?.message === 'string') return data.error.message;
  if (typeof data?.detail === 'string') return data.detail;
  if (Array.isArray(data?.detail) && data.detail.length > 0) {
    return data.detail[0]?.msg || data.detail[0]?.message || '';
  }
  if (typeof data?.message === 'string') return data.message;
  return '';
};

const PasswordResetShell = ({ children }) => (
  <main className="min-h-screen bg-[#090d16] text-zinc-50 px-4 py-8 flex items-center justify-center">
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
      <div className="absolute -top-28 -left-24 h-80 w-80 rounded-full bg-lime-300/10 blur-3xl" />
      <div className="absolute top-16 right-[-8rem] h-96 w-96 rounded-full bg-cyan-300/10 blur-3xl" />
      <div className="absolute inset-x-0 top-1/2 h-px bg-lime-300/20" />
      <div className="absolute left-1/2 top-0 h-full w-px bg-white/10" />
    </div>
    <section className="relative w-full max-w-lg rounded-2xl border border-white/10 bg-zinc-950/78 p-6 shadow-2xl shadow-black/30 backdrop-blur md:p-8">
      <div className="mb-8 flex justify-center">
        <BrandLogoWithWordmark size={44} />
      </div>
      {children}
    </section>
  </main>
);

const StatusMessage = ({ status, message }) => {
  if (!status || !message) return null;
  const tones = {
    success: 'border-lime-300/25 bg-lime-300/10 text-lime-100',
    error: 'border-red-200/25 bg-red-200/10 text-red-100',
    warning: 'border-amber-200/25 bg-amber-200/10 text-amber-100',
    loading: 'border-cyan-200/25 bg-cyan-200/10 text-cyan-100',
  };
  const Icon = status === 'success' ? CheckCircle2 : status === 'loading' ? RefreshCcw : CircleAlert;
  return (
    <div className={`flex items-start gap-3 rounded-lg border px-4 py-3 text-sm ${tones[status] || tones.error}`}>
      <Icon size={18} strokeWidth={1.8} className={status === 'loading' ? 'mt-0.5 animate-spin' : 'mt-0.5'} />
      <span>{message}</span>
    </div>
  );
};

export const PasswordResetPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const initialEmail = location.state?.email || readPendingResetEmail();
  const initialStep = location.pathname === '/reset-password' ? 'reset' : 'request';
  const [step, setStep] = useState(initialStep);
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState('');
  const [message, setMessage] = useState('');

  const normalizedEmail = email.trim();
  const normalizedCode = code.trim();
  const canRequest = normalizedEmail && !submitting;
  const canReset =
    normalizedEmail &&
    normalizedCode.length === 6 &&
    newPassword.length > 0 &&
    confirmPassword.length > 0 &&
    !submitting;

  const handleCodeChange = (event) => {
    setCode(event.target.value.replace(/\D/g, '').slice(0, 6));
    if (status) {
      setStatus('');
      setMessage('');
    }
  };

  const handleRequestCode = async (event) => {
    event.preventDefault();
    if (!canRequest) return;
    setSubmitting(true);
    setStatus('loading');
    setMessage(t('auth.passwordResetSending', 'Sending reset code...'));
    try {
      await auth.forgotPassword(normalizedEmail);
      savePendingResetEmail(normalizedEmail);
      setStep('reset');
      setStatus('success');
      setMessage(t('auth.passwordResetSent', 'If an account exists for this email, a reset code has been sent.'));
      navigate('/reset-password', { replace: true, state: { email: normalizedEmail } });
    } catch (error) {
      setStatus('error');
      setMessage(t('auth.passwordResetSendError', 'Could not send the reset code. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetPassword = async (event) => {
    event.preventDefault();
    if (!canReset) return;
    if (newPassword !== confirmPassword) {
      setStatus('error');
      setMessage(t('auth.passwordResetMismatch', 'Passwords do not match.'));
      return;
    }
    setSubmitting(true);
    setStatus('loading');
    setMessage(t('auth.passwordResetWorking', 'Changing your password...'));
    try {
      await auth.resetPassword({
        email: normalizedEmail,
        code: normalizedCode,
        new_password: newPassword,
      });
      clearPendingResetEmail();
      setStep('success');
      setStatus('success');
      setMessage(t('auth.passwordResetSuccess', 'Password changed. You can sign in with your new password.'));
      toast.success(t('auth.passwordResetSuccessShort', 'Password changed'));
    } catch (error) {
      const apiMessage = extractApiMessage(error);
      setStatus(apiMessage.toLowerCase().includes('expired') ? 'warning' : 'error');
      setMessage(apiMessage || t('auth.passwordResetError', 'The code is invalid, expired, or the password is not strong enough.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PasswordResetShell>
      <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full border border-lime-300/25 bg-lime-300/10 text-lime-300">
        {step === 'request' ? <Mail size={34} strokeWidth={1.7} /> : <KeyRound size={34} strokeWidth={1.7} />}
      </div>
      <div className="text-center">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-lime-200">
          {t('auth.passwordResetKicker', 'Account recovery')}
        </p>
        <h1 className="mt-3 text-3xl font-black tracking-normal text-white">
          {step === 'success'
            ? t('auth.passwordResetDoneTitle', 'Password changed')
            : t('auth.passwordResetTitle', 'Reset your password')}
        </h1>
        <p className="mt-3 text-sm leading-6 text-zinc-300">
          {step === 'request'
            ? t('auth.passwordResetRequestBody', 'Enter your email and we will send a 6-digit reset code.')
            : step === 'success'
            ? t('auth.passwordResetDoneBody', 'Use your new password the next time you sign in.')
            : t('auth.passwordResetCodeBody', 'Enter the reset code from your email and choose a new password.')}
        </p>
      </div>

      {step === 'request' ? (
        <form className="mt-7 grid gap-4" onSubmit={handleRequestCode}>
          <Field label={t('auth.email')} required>
            <Input
              type="email"
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
          </Field>
          <StatusMessage status={status} message={message} />
          <Button type="submit" variant="primary" size="lg" block loading={submitting} disabled={!canRequest}>
            {t('auth.sendResetCode', 'Send reset code')}
          </Button>
        </form>
      ) : null}

      {step === 'reset' ? (
        <form className="mt-7 grid gap-4" onSubmit={handleResetPassword}>
          <Field label={t('auth.email')} required>
            <Input
              type="email"
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
          </Field>
          <Field label={t('auth.passwordResetCode', 'Reset code')} required>
            <Input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={handleCodeChange}
              placeholder="000000"
              maxLength={6}
              required
            />
          </Field>
          <Field label={t('auth.newPassword', 'New password')} required>
            <Input
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              required
            />
          </Field>
          <Field label={t('auth.confirmPassword', 'Confirm password')} required>
            <Input
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              required
            />
          </Field>
          <StatusMessage status={status} message={message} />
          <Button type="submit" variant="primary" size="lg" block loading={submitting} disabled={!canReset}>
            {t('auth.resetPassword', 'Reset password')}
          </Button>
          <Button type="button" variant="secondary" size="lg" block loading={submitting && status === 'loading'} onClick={handleRequestCode} disabled={!normalizedEmail || submitting}>
            {t('auth.resendResetCode', 'Resend reset code')}
          </Button>
        </form>
      ) : null}

      {step === 'success' ? (
        <div className="mt-7 grid gap-4">
          <StatusMessage status={status} message={message} />
          <Button as={Link} to="/login" variant="primary" size="lg" block>
            {t('auth.backToSignIn', 'Back to sign in')}
          </Button>
        </div>
      ) : null}

      {step !== 'success' ? (
        <div className="mt-7 grid gap-3">
          <Button as={Link} to="/login" variant="secondary" size="lg" block>
            {t('auth.backToSignIn', 'Back to sign in')}
          </Button>
        </div>
      ) : null}
    </PasswordResetShell>
  );
};

export default PasswordResetPage;
